import { cn } from '@cobliteam/colib-ui/cn';
import type { Variants } from 'motion/react';
import { m } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

export interface TrendingUpIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface TrendingUpIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered: boolean;
}

const svgVariants: Variants = {
  animate: {
    x: 0,
    y: 0,
    translateX: [0, 2, 0],
    translateY: [0, -2, 0],
    transition: {
      duration: 0.5,
    },
  },
};

const pathVariants: Variants = {
  normal: {
    opacity: 1,
    pathLength: 1,
    transition: {
      duration: 0.4,
      opacity: { duration: 0.1 },
    },
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    pathOffset: [1, 0],
    transition: {
      duration: 0.4,
      opacity: { duration: 0.1 },
    },
  },
};

const arrowVariants: Variants = {
  normal: {
    opacity: 1,
    pathLength: 1,
    transition: {
      delay: 0.3,
      duration: 0.3,
      opacity: { duration: 0.1, delay: 0.3 },
    },
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    pathOffset: [0.5, 0],
    transition: {
      delay: 0.3,
      duration: 0.3,
      opacity: { duration: 0.1, delay: 0.3 },
    },
  },
};

const TrendingUpIcon = forwardRef<TrendingUpIconHandle, TrendingUpIconProps>(({ className, size = 28, isHovered, ...props }, _) => {
  const variant = isHovered ? 'animate' : 'normal';

  return (
    <div className={cn(className)} {...props}>
      <m.svg
        data-testid='lucide-trending-up'
        xmlns='http://www.w3.org/2000/svg'
        width={18}
        height={18}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        variants={svgVariants}
        initial='normal'
        animate={variant}
      >
        <m.polyline points='22 7 13.5 15.5 8.5 10.5 2 17' variants={pathVariants} initial='normal' animate={variant} />
        <m.polyline points='16 7 22 7 22 13' variants={arrowVariants} initial='normal' animate={variant} />
      </m.svg>
    </div>
  );
});

TrendingUpIcon.displayName = 'TrendingUpIcon';

export { TrendingUpIcon };
